import React from "react";
import { func, bool, string, number, shape, array } from "prop-types";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Modal,
} from "reactstrap";
import { Formik } from "formik";
import swal from "sweetalert2";
import { handleError } from "@/util/errorHandler";
import permissions from "@/api/permissions";
import Select from "react-select";
import LoadingOverlay from "./LoadingOverlay";
import {
  flatPages,
  getPagesByPermissions,
  getPermissionsByPages,
} from "@/util/endpoints";

const marginRight = {
  marginRight: "8px",
};

const badge = {
  padding: "8px 8px",
  marginRight: "8px",
  marginTop: "8px",
};

const badgeAction = {
  cursor: "pointer",
};

class ManageRoleModal extends React.Component {
  state = {
    roleName: "",
    isLoading: false,
    permissionOptions: [],
    permissionToAdd: null,
    rolePermissions: [],
  };

  textInput = React.createRef();

  baseState = {
    ...this.state,
  };

  componentDidMount() {
    this.getPermissions();
    if (this.props.role && this.props.role.permissions) {
      this.setState({
        rolePermissions: getPagesByPermissions(this.props.role.permissions).map(
          p => ({
            id: p.url,
            name: p.listName,
          }),
        ),
        roleName: this.props.role.name,
      });
    }
    if (this.props.isClone) {
      this.setState({
        roleName: "",
      });
    }
  }

  getPermissions = async () => {
    this.setState({
      permissionOptions: flatPages.map(p => ({
        value: p.url,
        label: p.listName,
      })),
    });
  };

  reset = () => {
    this.setState(this.baseState);
  };

  beforeClose = () => {
    this.reset();
    this.props.onModalClose();
  };

  validateFormik = ({ roleName }) => {
    const errors = {};
    this.setState({
      roleName: roleName,
    });

    if (roleName.length === 0) {
      errors.roleName = "Role name is required";
    }

    return errors;
  };

  onSendUpdateRoleRequest = async ({ roleName }) => {
    try {
      await permissions.createRole(roleName);

      await swal(
        "Success!",
        `Role ${roleName} successfully created.`,
        "success",
      );
      this.beforeClose();
    } catch (err) {
      handleError(err);
    }
  };

  handlePermissionSelect = perm => {
    this.setState({
      permissionToAdd: {
        id: perm.value,
        name: perm.label,
      },
    });
  };

  onAddPermission = () => {
    if (
      this.state.rolePermissions.find(
        p => p.id === this.state.permissionToAdd.id,
      )
    ) {
      this.setState({
        permissionToAdd: null,
      });
      this.textInput.current.state.value = null;
      return;
    }

    if (this.state.permissionToAdd !== null) {
      this.setState({
        permissionToAdd: null,
      });
      this.textInput.current.state.value = null;
      this.setState({
        rolePermissions: [
          ...this.state.rolePermissions,
          this.state.permissionToAdd,
        ],
      });
    }
  };

  onRemovePermission = permission => {
    this.setState({
      rolePermissions: this.state.rolePermissions.filter(
        p => permission.id !== p.id,
      ),
    });
  };

  saveChanges = async form => {
    this.setState({ isLoading: true });
    if (this.props.isEdit) {
      await permissions.setRolePermission(
        this.props.role.id,
        getPermissionsByPages(this.state.rolePermissions.map(p => p.name)),
      );
    } else {
      const { data } = await permissions.createRole(form.roleName);
      await permissions.setRolePermission(
        data.id,
        getPermissionsByPages(this.state.rolePermissions.map(p => p.name)),
      );
    }
    this.setState({ isLoading: false });

    this.beforeClose();
  };

  render() {
    return (
      <Modal isOpen={this.props.isOpen}>
        <Card>
          <LoadingOverlay loading={this.state.isLoading} />
          <CardHeader>
            <Button close onClick={this.beforeClose} />

            <span>
              {this.props.role && this.props.role.id
                ? this.props.isEdit
                  ? "Edit role"
                  : "Clone role"
                : "New role"}
            </span>
          </CardHeader>
          <Formik
            initialValues={{
              roleName: this.props.isEdit
                ? this.props.role.name
                : this.state.roleName,
            }}
            validate={this.validateFormik}
            onChange={this.validateFormik}
            onSubmit={this.saveChanges}>
            {({
              values,
              handleSubmit,
              handleChange,
              handleBlur,
              errors,
              setErrors,
            }) => (
              <Form onSubmit={handleSubmit}>
                <CardBody>
                  <FormGroup row>
                    <Label sm={3}>Role name</Label>
                    <Col sm={9}>
                      <Input
                        type="text"
                        name="roleName"
                        placeholder="Enter role name"
                        invalid={Boolean(errors.roleName)}
                        value={values.roleName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={this.props.isEdit}
                      />
                      {errors.roleName && (
                        <FormFeedback className="text-alert">
                          {errors.roleName}
                        </FormFeedback>
                      )}
                    </Col>
                  </FormGroup>

                  <Row>
                    <Col sm={10}>
                      <Select
                        onChange={this.handlePermissionSelect}
                        options={this.state.permissionOptions}
                        hideSelectedOptions={true}
                        ref={this.textInput}
                      />
                    </Col>
                    <Col sm={2}>
                      <Button
                        onClick={this.onAddPermission}
                        disabled={!this.state.permissionToAdd}>
                        Add
                      </Button>
                    </Col>
                  </Row>

                  {this.state.rolePermissions &&
                    this.state.rolePermissions.map(p => (
                      <Badge style={badge} color="light" key={p.id}>
                        <span>{p.name}</span>{" "}
                        <span
                          style={badgeAction}
                          onClick={() => {
                            this.onRemovePermission(p);
                          }}>
                          <i className="fa fa-times" />
                        </span>
                      </Badge>
                    ))}
                </CardBody>
                <CardFooter>
                  <Button style={marginRight} onClick={this.beforeClose}>
                    Close
                  </Button>
                  <Button
                    color="primary"
                    type="submit"
                    disabled={
                      this.state.rolePermissions.length <= 0 ||
                      this.state.roleName === ""
                    }>
                    {this.props.isEdit ? "Save changes" : "Create role"}
                  </Button>
                </CardFooter>
              </Form>
            )}
          </Formik>
        </Card>
      </Modal>
    );
  }
}

ManageRoleModal.defaultProps = {
  role: {
    id: null,
    name: null,
  },
  isEdit: false,
};

ManageRoleModal.propTypes = {
  isOpen: bool.isRequired,
  onModalClose: func.isRequired,
  isEdit: bool,
  isClone: bool,
  role: shape({
    id: number,
    name: string,
    permissions: array,
  }),
};

export default ManageRoleModal;
